import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthState, User, LoginCredentials, RegisterData } from '../types';
import { authService } from '../services/authApi';

// Auth Action Types
type AuthAction =
  | { type: 'AUTH_LOADING' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string; refreshToken: string } }
  | { type: 'AUTH_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User };

// Initial Auth State
const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isLoading: true,
  isAuthenticated: false,
};

// Auth Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_LOADING':
      return {
        ...state,
        isLoading: true,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        isLoading: false,
        isAuthenticated: true,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        refreshToken: null,
        isLoading: false,
        isAuthenticated: false,
      };
    case 'LOGOUT':
      return {
        ...initialState,
        isLoading: false,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
};

// Auth Context Type
interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (userData: Partial<User>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  setAuthFromOAuth: (token: string, user: User) => void;
  checkAuthStatus: () => Promise<void>;
}

// Create Auth Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider Props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check auth status on mount using cookies
  useEffect(() => {
    // Small delay to ensure localStorage is available
    const timer = setTimeout(() => {
      checkAuthStatus();
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: 'AUTH_LOADING' });
      const authResponse = await authService.login(credentials);
      
      authService.storeAuthData(authResponse);
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: authResponse.user,
          token: authResponse.token,
          refreshToken: authResponse.refreshToken
        }
      });
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE' });
      throw error;
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      dispatch({ type: 'AUTH_LOADING' });
      const authResponse = await authService.register(userData);
      
      authService.storeAuthData(authResponse);
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: authResponse.user,
          token: authResponse.token,
          refreshToken: authResponse.refreshToken
        }
      });
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE' });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      authService.clearAuthData();
      dispatch({ type: 'LOGOUT' });
    }
  };

  const updateProfile = async (userData: Partial<User>) => {
    try {
      const { user } = await authService.updateProfile(userData);
      dispatch({ type: 'UPDATE_USER', payload: user });
      
      // Update stored user data
      localStorage.setItem('user', JSON.stringify(user));
    } catch (error) {
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      await authService.changePassword(currentPassword, newPassword);
    } catch (error) {
      throw error;
    }
  };

  const checkAuthStatus = async () => {
    try {
      dispatch({ type: 'AUTH_LOADING' });

      // First try to get user from localStorage as fallback
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');

      if (storedUser && storedToken) {
        try {
          const user = JSON.parse(storedUser);
          // Verify with server
          const data = await authService.checkAuthStatus();
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: {
              user: data.user,
              token: storedToken,
              refreshToken: localStorage.getItem('refreshToken') || ''
            }
          });
          return;
        } catch (serverError) {
          console.warn('Server auth check failed, using stored user data:', serverError);
          // If server check fails but we have stored data, use it
          const user = JSON.parse(storedUser);
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: {
              user,
              token: storedToken,
              refreshToken: localStorage.getItem('refreshToken') || ''
            }
          });
          return;
        }
      }

      // If no stored data, try server
      const data = await authService.checkAuthStatus();
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: data.user,
          token: '', // Token is in cookie, not needed in state
          refreshToken: ''
        }
      });
    } catch (error) {
      console.error('Error checking auth status:', error);
      // Clear any invalid stored data
      authService.clearAuthData();
      dispatch({ type: 'AUTH_FAILURE' });
    }
  };

  const setAuthFromOAuth = (token: string, user: User) => {
    // Store auth data
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    
    // Update state
    dispatch({
      type: 'AUTH_SUCCESS',
      payload: {
        user,
        token,
        refreshToken: ''
      }
    });
  };

  const contextValue: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    setAuthFromOAuth,
    checkAuthStatus,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// Custom hook to use Auth Context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;